import { useState, useRef, useEffect } from "react";
import {
	rankAndFilterExercises,
	getYouTubeEmbedUrl,
	dedupeExercisesByScope,
	getExerciseScopeBadgeMeta,
	isEnduranceCategory,
	getConditioningQuantityPlaceholder,
} from "../lib/coachUtils";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { ScrollArea } from "./ui/scroll-area";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./ui/select";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from "./ui/dialog";
import {
	Dumbbell,
	Plus,
	PlusCircle,
	Search,
	Trash2,
	X,
	Wand2,
	Loader2,
	Play,
	ArrowUp,
	ArrowDown,
} from "lucide-react";
import {
	fetchAPI,
	formatRestDisplay,
	normalizeSmartRestInput,
	parseSmartRestSeconds,
	normalizeSmartRatioInput,
} from "../lib/utils";
import { toast } from "sonner";
import ConditioningResultSchemaEditor from "./ConditioningResultSchemaEditor";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { joinEmomItemLoads, splitEmomItemLoad } from "../lib/emomLoadUtils";

const CONDITIONING_TYPES = [
	{ value: "amrap", label: "AMRAP" },
	{ value: "for_time", label: "For Time" },
	{ value: "emom", label: "EMOM" },
	{ value: "hiit", label: "HIIT" },
	{ value: "free", label: "Custom" },
];

function formatSecondsToClock(value) {
	if (!Number.isFinite(Number(value)) || Number(value) <= 0) return "";
	const seconds = Number(value);
	const mins = Math.floor(seconds / 60);
	const rem = seconds % 60;
	return `${mins}:${String(rem).padStart(2, "0")}`;
}

function parseClockToSeconds(rawValue) {
	return parseSmartRestSeconds(rawValue);
}

function parseWorkRestRatio(rawValue) {
	const value = String(rawValue || "").trim();
	if (!value) return null;
	const parts = value.split(":");
	if (parts.length !== 2) return null;
	const work = Number(parts[0]);
	const rest = Number(parts[1]);
	if (
		!Number.isFinite(work) ||
		!Number.isFinite(rest) ||
		work <= 0 ||
		rest <= 0
	) {
		return null;
	}
	return { work, rest };
}

function createEmomRoundTemplate(overrides = {}) {
	return {
		round_id: `emom_round_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
		assignment_type: "cycle",
		label: "",
		content: "",
		input_mode: "library",
		items: [],
		...overrides,
	};
}

// --- HIIT: una riga per concetto (cicli, lavoro, recupero) ---------------
// La riga tiene l'etichetta a sinistra e, a destra, prima i pulsanti di
// modalita' e poi i campi. Tutti i contenuti della riga partono alla stessa
// altezza (la didascalia vuota di HiitField fa da spaziatore), cosi' il
// valore principale resta sempre nella stessa posizione quando la modalita'
// cambia.
function HiitRow({ label, children }) {
	return (
		<div className="grid grid-cols-1 md:grid-cols-[104px_minmax(0,1fr)] gap-1.5 md:gap-4 p-3">
			<span className="font-heading uppercase text-xs tracking-wider text-foreground md:pt-7">
				{label}
			</span>
			<div className="flex flex-wrap items-start gap-2 md:gap-3">{children}</div>
		</div>
	);
}

function HiitField({ label, className = "w-full md:w-[112px]", children }) {
	return (
		<div className={`space-y-1 shrink-0 ${className}`}>
			<span
				className={`text-[10px] text-muted-foreground uppercase tracking-wider ${
					label ? "block" : "hidden md:block"
				}`}
			>
				{label || "\u00A0"}
			</span>
			{children}
		</div>
	);
}

function HiitModeSwitch({ options, current, onSelect }) {
	return (
		<HiitField label="" className="w-full md:w-[272px]">
			<div className="grid grid-flow-col auto-cols-fr gap-1 p-[3px] rounded-sm bg-secondary h-9">
				{options.map((option) => {
					const isActive = current === option.value;
					return (
						<button
							key={option.value}
							type="button"
							onClick={() => onSelect(option.value)}
							aria-pressed={isActive}
							className={`rounded-sm px-1 text-[11px] font-heading uppercase tracking-wider transition-colors ${
								isActive
									? "bg-card text-yellow-400 border border-yellow-400/60"
									: "text-muted-foreground hover:text-foreground"
							}`}
						>
							{option.label}
						</button>
					);
				})}
			</div>
		</HiitField>
	);
}

export { CONDITIONING_TYPES, createEmomRoundTemplate };

export function ConditioningTemplateEditor({
	value,
	setValue,
	exercises,
	showNameField = true,
	resultSchemaFocusToken = 0,
	onExerciseCreated,
	schemaLocked = false,
	schemaLockedHint,
	isAdmin = false,
}) {
	const { user } = useAuth();
	const isCoachAiDisabled =
		user?.role === "coach" && !user?.is_admin && user?.ai_enabled === false;
	const { t } = useLanguage();
	const availableExercises = (exercises || []).filter(
		(ex) => !ex?._isConditioning && ex?.exercise_id,
	);

	const getExerciseLibraryScope = (exercise) =>
		exercise?.has_global_scope === true || exercise?.library_scope === "global"
			? "global"
			: "personal";

	const getExerciseScopeLabel = (exercise) =>
		getExerciseScopeBadgeMeta(exercise, t).label;

	const scopeFilteredExercises = dedupeExercisesByScope(availableExercises);

	// Conditioning exercise search state (AMRAP/For Time)
	const [condSearchQuery, setCondSearchQuery] = useState("");
	const [activeCondIdx, setActiveCondIdx] = useState(-1);
	const condSearchRef = useRef(null);

	// EMOM round exercise search state
	const [emomSearchQuery, setEmomSearchQuery] = useState("");
	const [activeEmomRoundIdx, setActiveEmomRoundIdx] = useState(null);
	const [videoPreviewState, setVideoPreviewState] = useState(null);

	// Create new exercise dialog state
	const [isCreatingExercise, setIsCreatingExercise] = useState(false);
	const [createExerciseIdx, setCreateExerciseIdx] = useState(-1);
	const [createExerciseEmomRoundIdx, setCreateExerciseEmomRoundIdx] =
		useState(-1);
	const [highlightResultSchema, setHighlightResultSchema] = useState(false);
	const resultSchemaRef = useRef(null);
	const [newExerciseDetails, setNewExerciseDetails] = useState({
		name: "",
		description: "",
		video_url: "",
		image_url: "",
		category: "strength",
	});
	const [savingExercise, setSavingExercise] = useState(false);

	// AI parsing state
	const [aiParsing, setAiParsing] = useState(false);

	// Close conditioning dropdown on outside click
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (
				condSearchRef.current &&
				!condSearchRef.current.contains(event.target)
			) {
				setActiveCondIdx(-1);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	useEffect(() => {
		if (!resultSchemaFocusToken) return;
		const node = resultSchemaRef.current;
		if (!node) return;

		node.scrollIntoView({ behavior: "smooth", block: "center" });
		setHighlightResultSchema(true);
		const timeoutId = setTimeout(() => {
			setHighlightResultSchema(false);
		}, 2400);

		return () => clearTimeout(timeoutId);
	}, [resultSchemaFocusToken]);

	const updateField = (field, val) => {
		setValue((prev) => ({ ...prev, [field]: val }));
	};

	const updateHiitClockField = (clockField, secondsField, rawValue) => {
		const parsedSeconds = parseClockToSeconds(rawValue);
		setValue((prev) => ({
			...prev,
			[clockField]: rawValue,
			[secondsField]: parsedSeconds,
		}));
	};

	const handleCustomConditioningTab = (e) => {
		if (e.key !== "Tab") return;
		e.preventDefault();
		const target = e.target;
		const currentText = value.conditioning_free_text ?? "";
		const start = target.selectionStart ?? currentText.length;
		const end = target.selectionEnd ?? currentText.length;

		if (start === end) {
			const indent = "\t";
			const nextText =
				currentText.slice(0, start) + indent + currentText.slice(end);
			updateField("conditioning_free_text", nextText);
			requestAnimationFrame(() => {
				target.selectionStart = start + indent.length;
				target.selectionEnd = start + indent.length;
			});
			return;
		}

		const startLineIndex = currentText.lastIndexOf("\n", start - 1) + 1;
		const selectedBlock = currentText.slice(startLineIndex, end);
		const selectedLineCount = selectedBlock.split("\n").length;
		const indentedBlock = "\t" + selectedBlock.replace(/\n/g, "\n\t");
		const nextText =
			currentText.slice(0, startLineIndex) +
			indentedBlock +
			currentText.slice(end);

		updateField("conditioning_free_text", nextText);
		requestAnimationFrame(() => {
			target.selectionStart = start + 1;
			target.selectionEnd = end + selectedLineCount;
		});
	};

	// --- AI Parse conditioning (magic wand) ---
	const handleParseConditioning = async () => {
		if (isCoachAiDisabled) {
			toast.error(t("pbAiDisabledForCoach"));
			return;
		}
		const text = value.conditioning_free_text?.trim();
		if (!text) {
			toast.error(t("pbAiWriteConditioningFirst"));
			return;
		}
		setAiParsing(true);
		try {
			const exerciseNames = availableExercises
				.map((e) => e.name)
				.filter(Boolean);
			const res = await fetchAPI("/ai/parse-conditioning", {
				method: "POST",
				body: JSON.stringify({
					free_text: text,
					exercise_names: exerciseNames,
				}),
			});
			const r = res.result;
			if (!r || !r.conditioning_type) {
				toast.error(t("pbAiParseFailed"));
				return;
			}

			// Build EMOM interval string from interval_seconds
			let emomInterval = "";
			if (r.conditioning_type === "emom" && r.interval_seconds) {
				const mins = Math.floor(r.interval_seconds / 60);
				const secs = r.interval_seconds % 60;
				emomInterval = `${mins}:${secs.toString().padStart(2, "0")}`;
			}

			// Build emom_rounds with proper IDs
			let emomRounds = [];
			if (r.conditioning_type === "emom" && r.emom_rounds?.length) {
				emomRounds = r.emom_rounds.map((er) =>
					createEmomRoundTemplate({
						assignment_type: er.assignment_type || "cycle",
						input_mode: "library",
						items: (er.items || []).map((item) => ({
							exercise_id: item.exercise_id || "",
							exercise_name: item.exercise_name || "",
							reps: item.reps || "",
							load: item.load || "",
							...splitEmomItemLoad(item.load),
						})),
					}),
				);
			}

			setValue((prev) => ({
				...prev,
				conditioning_type: r.conditioning_type,
				name: r.name || prev.name || "",
				time_cap_seconds: r.time_cap_seconds || null,
				rounds: r.rounds ?? null,
				interval_seconds: r.interval_seconds || null,
				emom_conditioning_interval: emomInterval,
				conditioning_exercises: (r.conditioning_exercises || []).map((ce) => ({
					exercise_id: ce.exercise_id || "",
					exercise_name: ce.exercise_name || "",
					reps: ce.reps || "",
					load: ce.load || "",
					notes: ce.notes || "",
				})),
				conditioning_free_text: "",
				emom_rounds:
					emomRounds.length > 0 ? emomRounds : prev.emom_rounds || [],
				notes: r.notes || prev.notes || "",
			}));
			toast.success(`${t("pbAiConvertedTo")} ${r.conditioning_type.toUpperCase()}!`);
		} catch (error) {
			toast.error(error.message || t("pbAiParseError"));
		} finally {
			setAiParsing(false);
		}
	};

	// --- Exercise row helpers (AMRAP/For Time) ---
	const addExerciseRow = () => {
		setValue((prev) => ({
			...prev,
			conditioning_exercises: [
				...(prev.conditioning_exercises || []),
				{ exercise_id: "", exercise_name: "", reps: "", load: "", notes: "" },
			],
		}));
	};

	const updateExerciseRow = (index, field, val) => {
		setValue((prev) => {
			const rows = [...(prev.conditioning_exercises || [])];
			rows[index] = { ...rows[index], [field]: val };
			return { ...prev, conditioning_exercises: rows };
		});
	};

	const removeExerciseRow = (index) => {
		setValue((prev) => {
			const rows = [...(prev.conditioning_exercises || [])];
			rows.splice(index, 1);
			return { ...prev, conditioning_exercises: rows };
		});
		if (activeCondIdx === index) setActiveCondIdx(-1);
	};

	const moveExerciseRow = (index, direction) => {
		setValue((prev) => {
			const rows = [...(prev.conditioning_exercises || [])];
			const target = index + direction;
			if (target < 0 || target >= rows.length) return prev;
			[rows[index], rows[target]] = [rows[target], rows[index]];
			return { ...prev, conditioning_exercises: rows };
		});
		setActiveCondIdx((prevIdx) => {
			if (prevIdx === index) return index + direction;
			if (prevIdx === index + direction) return index;
			return prevIdx;
		});
	};

	// --- Create new exercise handler ---
	const handleOpenCreateExercise = (ci) => {
		setNewExerciseDetails({
			name: condSearchQuery,
			description: "",
			video_url: "",
			image_url: "",
			category: "strength",
		});
		setCreateExerciseIdx(ci);
		setCreateExerciseEmomRoundIdx(-1);
		setIsCreatingExercise(true);
		setActiveCondIdx(-1);
	};

	const handleOpenCreateExerciseForEmom = (ri) => {
		setNewExerciseDetails({
			name: emomSearchQuery,
			description: "",
			video_url: "",
			image_url: "",
			category: "strength",
		});
		setCreateExerciseIdx(-1);
		setCreateExerciseEmomRoundIdx(ri);
		setIsCreatingExercise(true);
		setActiveEmomRoundIdx(null);
	};

	const handleCreateExercise = async () => {
		if (!newExerciseDetails.name.trim()) {
			toast.error(t("pbEnterExerciseName"));
			return;
		}
		setSavingExercise(true);
		try {
			const result = await fetchAPI("/exercises", {
				method: "POST",
				body: JSON.stringify({
					name: newExerciseDetails.name.trim(),
					description: newExerciseDetails.description || null,
					video_url: newExerciseDetails.video_url || null,
					image_url: newExerciseDetails.image_url || null,
					category: newExerciseDetails.category,
					tags: [],
				}),
			});

			if (createExerciseEmomRoundIdx >= 0) {
				setValue((prev) => {
					const updated = [...(prev.emom_rounds || [])];
					const items = [
						...(updated[createExerciseEmomRoundIdx]?.items || []),
						{
							exercise_id: result.exercise_id,
							exercise_name: result.name,
							reps: "",
							load: "",
						},
					];
					updated[createExerciseEmomRoundIdx] = {
						...updated[createExerciseEmomRoundIdx],
						items,
					};
					return { ...prev, emom_rounds: updated };
				});
			} else if (createExerciseIdx >= 0) {
				setValue((prev) => {
					const ces = [...(prev.conditioning_exercises || [])];
					if (createExerciseIdx < ces.length) {
						ces[createExerciseIdx] = {
							...ces[createExerciseIdx],
							exercise_id: result.exercise_id,
							exercise_name: result.name,
						};
					}
					return { ...prev, conditioning_exercises: ces };
				});
			}

			setIsCreatingExercise(false);
			setCreateExerciseIdx(-1);
			setCondSearchQuery("");
			setCreateExerciseEmomRoundIdx(-1);
			setEmomSearchQuery("");
			toast.success(
				`${t("exerciseLabel")} "${result.name}" ${t("pbExerciseAddedToPersonalLibrary")}`,
			);
			if (onExerciseCreated) onExerciseCreated(result);
		} catch (error) {
			toast.error(`${t("createError")}: ${error.message}`);
		} finally {
			setSavingExercise(false);
		}
	};

	// --- EMOM round helpers ---
	const addEmomRound = () => {
		setValue((prev) => ({
			...prev,
			emom_rounds: [...(prev.emom_rounds || []), createEmomRoundTemplate()],
		}));
	};

	const updateEmomRound = (ri, field, val) => {
		setValue((prev) => {
			const updated = [...(prev.emom_rounds || [])];
			updated[ri] = { ...updated[ri], [field]: val };
			return { ...prev, emom_rounds: updated };
		});
	};

	const removeEmomRound = (ri) => {
		setValue((prev) => {
			const updated = [...(prev.emom_rounds || [])];
			updated.splice(ri, 1);
			return { ...prev, emom_rounds: updated };
		});
	};

	const moveEmomRound = (ri, direction) => {
		setValue((prev) => {
			const updated = [...(prev.emom_rounds || [])];
			const target = ri + direction;
			if (target < 0 || target >= updated.length) return prev;
			[updated[ri], updated[target]] = [updated[target], updated[ri]];
			return { ...prev, emom_rounds: updated };
		});
		setActiveEmomRoundIdx((prevIdx) => {
			if (prevIdx === ri) return ri + direction;
			if (prevIdx === ri + direction) return ri;
			return prevIdx;
		});
	};

	const addEmomRoundItem = (ri, exercise) => {
		setValue((prev) => {
			const updated = [...(prev.emom_rounds || [])];
			const items = [
				...(updated[ri].items || []),
				{
					exercise_id: exercise.exercise_id,
					exercise_name: exercise.name,
					reps: "",
					load: "",
					load_male: "",
					load_female: "",
				},
			];
			updated[ri] = { ...updated[ri], items };
			return { ...prev, emom_rounds: updated };
		});
		setEmomSearchQuery("");
		setActiveEmomRoundIdx(null);
	};

	const updateEmomRoundItem = (ri, ii, field, val) => {
		setValue((prev) => {
			const updated = [...(prev.emom_rounds || [])];
			const items = [...updated[ri].items];
			const currentItem = items[ii];
			const nextItem = { ...currentItem, [field]: val };
			if (field === "load") {
				const split = splitEmomItemLoad(val);
				nextItem.load_male = split.load_male;
				nextItem.load_female = split.load_female;
			}
			items[ii] = nextItem;
			updated[ri] = { ...updated[ri], items };
			return { ...prev, emom_rounds: updated };
		});
	};

	const removeEmomRoundItem = (ri, ii) => {
		setValue((prev) => {
			const updated = [...(prev.emom_rounds || [])];
			const items = [...updated[ri].items];
			items.splice(ii, 1);
			updated[ri] = { ...updated[ri], items };
			return { ...prev, emom_rounds: updated };
		});
	};

	const openExerciseVideoPreview = (contextKey, exercise) => {
		const embedUrl = getYouTubeEmbedUrl(exercise?.video_url);
		if (!embedUrl) {
			toast.error(t("pbVideoUnavailable"));
			return;
		}

		setVideoPreviewState({
			contextKey,
			exerciseName:
				exercise?.name || exercise?.exercise_name || t("exerciseLabel"),
			embedUrl,
		});
	};

	const closeExerciseVideoPreview = () => {
		setVideoPreviewState(null);
	};

	const renderInlineVideoPreview = (contextKey) => {
		if (!videoPreviewState || videoPreviewState.contextKey !== contextKey) {
			return null;
		}

		return (
			<div
				className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 p-4"
				onClick={closeExerciseVideoPreview}
			>
				<div
					className="w-full max-w-3xl rounded-sm border border-volt/40 bg-card p-3 space-y-3"
					onClick={(event) => event.stopPropagation()}
				>
					<div className="flex items-center justify-between gap-2">
						<p className="text-[10px] uppercase tracking-wider text-muted-foreground truncate">
							Preview video: {videoPreviewState.exerciseName}
						</p>
						<Button
							type="button"
							variant="ghost"
							size="icon"
							className="h-7 w-7 text-muted-foreground hover:text-foreground"
							onClick={closeExerciseVideoPreview}
						>
							<X className="w-4 h-4" />
						</Button>
					</div>
					<div className="overflow-hidden rounded-sm border border-border bg-black/40">
						<iframe
							src={videoPreviewState.embedUrl}
							title={`${t("pbVideoPreviewOfLabel")} ${videoPreviewState.exerciseName}`}
							className="w-full aspect-video"
							allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
							allowFullScreen
						/>
					</div>
				</div>
			</div>
		);
	};

	const hiitWorkMode = value.hiit_work_mode || "for_time";
	const hiitRestMode = value.hiit_rest_mode || "fixed";
	const hiitWorkModeOptions = [
		{ value: "for_time", label: "For Time" },
		{ value: "amrap", label: "AMRAP" },
		{ value: "custom", label: t("pbCustomLabel") },
	];
	const hiitRestModeOptions = [
		{ value: "fixed", label: t("pbHiitRestFixed") },
		{ value: "ratio", label: t("pbHiitRestRatio") },
	];

	// Anteprima: gli stessi numeri che finiranno nella riga settimana e nella
	// dashboard atleta, calcolati qui mentre il coach scrive.
	const hiitWorkSeconds = (() => {
		const stored = Number(value.hiit_work_seconds);
		if (Number.isFinite(stored) && stored > 0) return Math.round(stored);
		return parseClockToSeconds(value.hiit_work_interval);
	})();
	const hiitRatio = parseWorkRestRatio(value.hiit_rest_ratio);
	const hiitRestSeconds = (() => {
		if (hiitRestMode === "ratio") {
			if (!hiitRatio || !hiitWorkSeconds || hiitWorkSeconds <= 0) return null;
			return Math.round((hiitWorkSeconds * hiitRatio.rest) / hiitRatio.work);
		}
		const stored = Number(value.hiit_rest_seconds);
		if (Number.isFinite(stored) && stored > 0) return Math.round(stored);
		return parseClockToSeconds(value.hiit_rest_interval);
	})();
	const hiitCycles = Number(value.hiit_cycles);
	const hiitHasCycles = Number.isFinite(hiitCycles) && hiitCycles > 0;
	const hiitTotalSeconds =
		hiitHasCycles && hiitWorkSeconds > 0 && hiitRestSeconds > 0
			? hiitCycles * hiitWorkSeconds + (hiitCycles - 1) * hiitRestSeconds
			: null;
	// Niente avvisi su un blocco ancora vuoto: compaiono appena si tocca un campo.
	const hiitTouched = Boolean(
		hiitHasCycles ||
			hiitWorkSeconds ||
			hiitRestSeconds ||
			String(value.hiit_work_rounds || "").trim() ||
			String(value.hiit_work_custom_text || "").trim() ||
			String(value.hiit_rest_ratio || "").trim(),
	);

	return (
		<div className="space-y-3">
			<Label className="label-tactical">{t("libraryExercisesLabel")}</Label>

			{showNameField && isAdmin && (
				<div className="space-y-2">
					<Label className="label-tactical">{t("libraryScope")}</Label>
					<Select
						value={value.library_scope || "personal"}
						onValueChange={(val) => updateField("library_scope", val)}
					>
						<SelectTrigger className="bg-secondary border-transparent rounded-sm">
							<SelectValue />
						</SelectTrigger>
						<SelectContent className="bg-card border-border">
							<SelectItem value="personal">
								{t("scopePersonalLabel")}
							</SelectItem>
							<SelectItem value="global">{t("scopeGlobalLabel")}</SelectItem>
						</SelectContent>
					</Select>
				</div>
			)}

			{showNameField && (
				<div className="space-y-2">
					<Label className="label-tactical">{t("circuitName")}</Label>
					<Input
						value={value.name || ""}
						onChange={(e) => updateField("name", e.target.value)}
						className="bg-secondary border-transparent rounded-sm"
						placeholder={t("pbCondNamePlaceholder")}
					/>
				</div>
			)}

			{/* Type selector */}
			<div className="space-y-2">
				<Label className="label-tactical">{t("pbWodType")}</Label>
				<div className="grid grid-cols-3 gap-2">
					{CONDITIONING_TYPES.map((ct) => (
						<button
							type="button"
							key={ct.value}
							onClick={() =>
								setValue((prev) => ({
									...prev,
									conditioning_type: ct.value,
									...(ct.value === "emom"
										? {
												interval_seconds: prev.interval_seconds || 60,
												emom_conditioning_interval:
													prev.emom_conditioning_interval || "1:00",
											}
										: {}),
								}))
							}
							className={`p-2 rounded-sm border text-xs text-center font-heading uppercase transition-colors ${
								value.conditioning_type === ct.value
									? "border-yellow-400 bg-yellow-400/10 text-yellow-400"
									: "border-border text-muted-foreground hover:border-yellow-400/50"
							}`}
						>
							{ct.label}
						</button>
					))}
				</div>
			</div>

			{/* AMRAP config */}
			{value.conditioning_type === "amrap" && (
				<div className="space-y-1">
					<span className="text-xs text-muted-foreground">{t("pbTimeMin")}</span>
					<Input
						type="text"
						inputMode="numeric"
						value={
							value.time_cap_seconds
								? Math.floor(value.time_cap_seconds / 60)
								: ""
						}
						onChange={(e) =>
							updateField(
								"time_cap_seconds",
								e.target.value === "" ? null : Number(e.target.value) * 60,
							)
						}
						className="bg-secondary border-transparent rounded-sm"
						placeholder={t("pbEnterDurationMin")}
					/>
				</div>
			)}

			{/* For Time config */}
			{value.conditioning_type === "for_time" && (
				<div className="grid grid-cols-12 gap-3">
					<div className="space-y-1 col-span-8">
						<span className="text-xs text-muted-foreground">{t("roundsLabel")}</span>
						<Input
							type="text"
							value={value.rounds ?? ""}
							onChange={(e) =>
								updateField(
									"rounds",
									e.target.value === "" ? null : e.target.value,
								)
							}
							className="bg-secondary border-transparent rounded-sm"
							placeholder={t("pbEnterRoundsOrScheme")}
						/>
					</div>
					<div className="space-y-1 col-span-4">
						<span className="text-[10px] text-muted-foreground uppercase tracking-wider">
							{t("pbTimeCapMin")}
						</span>
						<Input
							type="text"
							inputMode="numeric"
							value={
								value.time_cap_seconds
									? Math.floor(value.time_cap_seconds / 60)
									: ""
							}
							onChange={(e) =>
								updateField(
									"time_cap_seconds",
									e.target.value === "" ? null : Number(e.target.value) * 60,
								)
							}
							className="bg-secondary border-transparent rounded-sm"
							placeholder={t("pbEg20")}
						/>
					</div>
				</div>
			)}

			{/* EMOM config */}
			{value.conditioning_type === "emom" && (
				<div className="space-y-4">
					<div className="grid grid-cols-2 gap-3">
						<div className="space-y-1">
							<span className="text-[10px] text-muted-foreground uppercase tracking-wider">
								{t("roundsLabel")}
							</span>
							<Input
								type="text"
								inputMode="numeric"
								value={value.rounds ?? ""}
								onChange={(e) =>
									updateField(
										"rounds",
										e.target.value === "" ? null : e.target.value,
									)
								}
								className="bg-secondary border-transparent rounded-sm"
								placeholder="10"
							/>
						</div>
						<div className="space-y-1">
							<span className="text-[10px] text-muted-foreground uppercase tracking-wider">
								{t("pbRoundDuration")}
							</span>
							<Input
								type="text"
								value={value.emom_conditioning_interval ?? ""}
								onChange={(e) =>
									updateField("emom_conditioning_interval", e.target.value)
								}
								className="bg-secondary border-transparent rounded-sm"
								placeholder="1:00"
							/>
						</div>
					</div>

					{/* Round Builder EMOM */}
					<div className="space-y-2 rounded-sm border border-volt/30 bg-volt/5 p-3">
						<div className="flex items-center justify-between">
							<span className="text-[10px] uppercase tracking-wider text-volt font-heading">
								{t("pbEmomRoundBuilder")}
							</span>
							<Button
								variant="outline"
								size="sm"
								className="h-7 text-xs rounded-sm"
								onClick={addEmomRound}
							>
								<Plus className="w-3 h-3 mr-1" />
								{t("pbAddRound")}
							</Button>
						</div>
						{(value.emom_rounds || []).map((round, ri) => (
							<div
								key={round.round_id || ri}
								className="space-y-2 rounded-sm border border-border/60 p-2 bg-background/60"
							>
								<div className="flex items-center gap-2">
									<div className="flex flex-col">
										<button
											type="button"
											disabled={ri === 0}
											onClick={() => moveEmomRound(ri, -1)}
											className="text-[11px] text-muted-foreground hover:text-foreground disabled:opacity-30 leading-none px-1"
											aria-label={t("pbMoveRoundUp")}
										>
											▲
										</button>
										<button
											type="button"
											disabled={ri === (value.emom_rounds || []).length - 1}
											onClick={() => moveEmomRound(ri, 1)}
											className="text-[11px] text-muted-foreground hover:text-foreground disabled:opacity-30 leading-none px-1"
											aria-label={t("pbMoveRoundDown")}
										>
											▼
										</button>
									</div>

									<Select
										value={round.assignment_type || "cycle"}
										onValueChange={(v) =>
											updateEmomRound(ri, "assignment_type", v)
										}
									>
										<SelectTrigger className="h-7 w-[6.5rem] flex-shrink-0 bg-secondary border-transparent rounded-sm text-[10px] uppercase tracking-wider">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="cycle">{t("pbRoundLabel")}</SelectItem>
											<SelectItem value="odd">{t("pbEmomOddRound")}</SelectItem>
											<SelectItem value="even">{t("pbEmomEvenRound")}</SelectItem>
											<SelectItem value="specific">{t("pbEmomSpecificRound")}</SelectItem>
											<SelectItem value="custom">{t("pbCustomLabel")}</SelectItem>
										</SelectContent>
									</Select>

									<Input
										value={round.label || ""}
										onChange={(e) =>
											updateEmomRound(ri, "label", e.target.value)
										}
										className="flex-1 min-w-0 h-7 text-[10px] bg-transparent border-transparent focus-visible:ring-0 focus-visible:bg-secondary/50 rounded-sm placeholder:text-muted-foreground/40"
										placeholder={t("pbRoundTitlePlaceholder")}
									/>
									<div className="flex bg-secondary/50 rounded-sm p-0.5 border border-border/40 flex-shrink-0">
										<button
											type="button"
											onClick={() =>
												updateEmomRound(ri, "input_mode", "library")
											}
											className={`px-2 py-1 text-[11px] uppercase tracking-tighter rounded-sm transition-all ${
												round.input_mode === "library"
													? "bg-volt text-white shadow-sm"
													: "text-muted-foreground hover:text-foreground"
											}`}
										>
											{t("pbLibraryLabel")}
										</button>
										<button
											type="button"
											onClick={() =>
												updateEmomRound(ri, "input_mode", "custom")
											}
											className={`px-2 py-1 text-[11px] uppercase tracking-tighter rounded-sm transition-all ${
												round.input_mode === "custom"
													? "bg-volt text-white shadow-sm"
													: "text-muted-foreground hover:text-foreground"
											}`}
										>
											{t("pbCustomLabel")}
										</button>
									</div>

									<Button
										variant="ghost"
										size="icon"
										className="h-6 w-6 ml-1"
										onClick={() => removeEmomRound(ri)}
									>
										<X className="w-3 h-3 text-destructive" />
									</Button>
								</div>

								{round.input_mode === "library" ? (
									<div className="space-y-2 pt-1 border-t border-border/20">
										{(round.items || []).map((item, ii) => {
											const itemCategory = item.exercise_id
												? availableExercises.find(
														(ex) => ex.exercise_id === item.exercise_id,
													)?.category
												: null;
											const isEnduranceItem = isEnduranceCategory(itemCategory);
											const itemLoadValue =
												item.load ??
												joinEmomItemLoads(item.load_male, item.load_female);
											// Load shown for everything except endurance (ergometers have no weight).
											const showItemLoad = !isEnduranceItem;
											return (
												<div
													key={ii}
													className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_28px] md:grid-cols-[minmax(160px,2.2fr)_minmax(60px,0.6fr)_minmax(116px,1fr)_24px] gap-1.5 items-center"
												>
													<div className="col-span-3 md:col-span-1 text-[11px] bg-background/40 py-1 px-2 rounded-sm border border-border/20 truncate">
														{item.exercise_name}
													</div>
													<Input
														value={item.reps || ""}
														onChange={(e) =>
															updateEmomRoundItem(
																ri,
																ii,
																"reps",
																e.target.value,
															)
														}
														className="h-6 text-[10px] bg-secondary border-transparent"
														placeholder={getConditioningQuantityPlaceholder(
															itemCategory,
														)}
														title={
															isEnduranceItem
																? t("pbCaloriesMaleFemale")
																: t("reps")
														}
													/>
													{showItemLoad ? (
														<Input
															value={itemLoadValue}
															onChange={(e) =>
																updateEmomRoundItem(
																	ri,
																	ii,
																	"load",
																	e.target.value,
																)
															}
															className="h-6 text-[10px] bg-secondary border-transparent text-volt"
															placeholder={t("pbLoadMaleFemale")}
															aria-label={t("pbLoadMaleFemale")}
														/>
													) : (
														<div aria-hidden="true" />
													)}
													<Button
														variant="ghost"
														size="icon"
														className="h-5 w-5 text-muted-foreground hover:text-destructive"
														onClick={() => removeEmomRoundItem(ri, ii)}
													>
														<Trash2 className="w-3 h-3" />
													</Button>
													{/* Le note di movimento non si scrivono piu' (la nota
													    e' una, su tutto l'esercizio), ma quelle gia' nei
													    programmi l'atleta le legge: qui si vedono e si
													    tolgono, e il campo sparisce appena sono vuote. */}
													{String(item.notes || "").trim() && (
														<div className="col-span-3 md:col-span-4 flex items-start gap-1">
															<span className="text-[10px] text-muted-foreground italic">
																{t("pbMovementNote")}:{" "}
																{String(item.notes).trim()}
															</span>
															<Button
																variant="ghost"
																size="icon"
																className="h-5 w-5 flex-shrink-0 text-muted-foreground hover:text-destructive"
																title={t("pbRemoveMovementNote")}
																onClick={() =>
																	updateEmomRoundItem(ri, ii, "notes", "")
																}
															>
																<X className="w-3 h-3" />
																<span className="sr-only">
																	{t("pbRemoveMovementNote")}
																</span>
															</Button>
														</div>
													)}
												</div>
											);
										})}

										<div className="relative">
											<div className="flex items-center gap-1.5">
												<Search className="w-3.5 h-3.5 text-muted-foreground absolute left-2" />
												<Input
													value={
														activeEmomRoundIdx === ri ? emomSearchQuery : ""
													}
													onChange={(e) => {
														setEmomSearchQuery(e.target.value);
														setActiveEmomRoundIdx(ri);
													}}
													onFocus={() => setActiveEmomRoundIdx(ri)}
													placeholder={t("searchExercisePlaceholder")}
													className="h-7 text-[10px] pl-7 bg-secondary/80 border-transparent rounded-sm"
												/>
											</div>

											{activeEmomRoundIdx === ri &&
												emomSearchQuery.length > 0 && (
													<div className="absolute top-full left-0 right-0 z-50 mt-1 bg-popover border border-border rounded-sm shadow-xl">
														<ScrollArea className="h-52">
															{rankAndFilterExercises(
																scopeFilteredExercises,
																emomSearchQuery,
															)
																.slice(0, 30)
																.map((ex) => (
																	<div
																		key={ex.exercise_id}
																		className="flex items-center gap-1 px-1 border-b border-border/40 last:border-0"
																	>
																		<button
																			type="button"
																			onClick={() => {
																				addEmomRoundItem(ri, ex);
																				setVideoPreviewState(null);
																			}}
																			className="flex-1 text-left px-2 py-1.5 text-[11px] hover:bg-volt/10 transition-colors rounded-sm"
																		>
																			<div className="flex items-center justify-between gap-2">
																				<span className="truncate">
																					{ex.name}
																				</span>
																				<Badge
																					className={`border-0 text-[11px] px-1.5 py-0 ${
																						getExerciseLibraryScope(ex) ===
																						"global"
																							? "bg-amber-500/20 text-amber-300"
																							: "bg-emerald-500/20 text-emerald-300"
																					}`}
																				>
																					{getExerciseScopeLabel(ex)}
																				</Badge>
																			</div>
																		</button>
																		<Button
																			type="button"
																			variant="ghost"
																			size="icon"
																			className="h-6 w-6 text-muted-foreground hover:text-volt"
																			disabled={
																				!getYouTubeEmbedUrl(ex.video_url)
																			}
																			onClick={(event) => {
																				event.stopPropagation();
																				openExerciseVideoPreview(
																					`conditioning-emom-round-${ri}`,
																					ex,
																				);
																			}}
																		>
																			<Play className="w-3 h-3" />
																		</Button>
																	</div>
																))}
															<button
																type="button"
																onClick={() =>
																	handleOpenCreateExerciseForEmom(ri)
																}
																className="w-full text-left px-2 py-2 text-[11px] text-volt hover:bg-volt/10 transition-colors flex items-center gap-1.5 border-t border-border/40"
															>
																<Plus className="w-3 h-3" />
																{`${t("add")} "${emomSearchQuery}" ${t("pbAddToPersonalLibrary")}`}
															</button>
														</ScrollArea>
													</div>
												)}
											{renderInlineVideoPreview(
												`conditioning-emom-round-${ri}`,
											)}
										</div>
									</div>
								) : (
									<Textarea
										value={round.content || ""}
										onChange={(e) =>
											updateEmomRound(ri, "content", e.target.value)
										}
										className="bg-secondary border-transparent rounded-sm text-xs min-h-[56px] resize-y md:resize overflow-auto"
										placeholder={t("pbRoundContentPlaceholder")}
									/>
								)}
							</div>
						))}
					</div>

					{/* EMOM notes */}
					<div className="space-y-1">
						<span className="text-[10px] text-muted-foreground uppercase tracking-wider">
							{t("pbEmomNotes")}
						</span>
						<Textarea
							value={value.notes ?? ""}
							onChange={(e) => updateField("notes", e.target.value)}
							className="bg-secondary border-transparent rounded-sm text-xs min-h-[48px] resize-none"
							placeholder={t("pbEmomNotesPlaceholder")}
						/>
					</div>
				</div>
			)}

			{/* HIIT config — una riga per concetto: cicli, lavoro, recupero */}
			{value.conditioning_type === "hiit" && (
				<div className="rounded-sm border border-border divide-y divide-border">
					{/* Cicli */}
					<HiitRow label={t("pbCycles")}>
						<HiitField label="">
							<Input
								type="text"
								inputMode="numeric"
								value={value.hiit_cycles ?? ""}
								onChange={(e) =>
									updateField(
										"hiit_cycles",
										e.target.value === "" ? null : Number(e.target.value),
									)
								}
								className="bg-secondary border-transparent rounded-sm h-9"
								placeholder="8"
							/>
						</HiitField>
					</HiitRow>

					{/* Lavoro */}
					<HiitRow label={t("hiitWorkLabel")}>
						<HiitModeSwitch
							options={hiitWorkModeOptions}
							current={hiitWorkMode}
							onSelect={(nextMode) => updateField("hiit_work_mode", nextMode)}
						/>

						{hiitWorkMode === "for_time" && (
							<>
								<HiitField label={t("pbHiitTimeCap")}>
									<Input
										type="text"
										value={
											value.hiit_work_interval ??
											formatSecondsToClock(value.hiit_work_seconds)
										}
										onChange={(e) =>
											updateHiitClockField(
												"hiit_work_interval",
												"hiit_work_seconds",
												e.target.value,
											)
										}
										className="bg-secondary border-transparent rounded-sm h-9"
										placeholder="8:00"
									/>
								</HiitField>
								<HiitField
									label={t("pbHiitScheme")}
									className="w-full md:w-[220px]"
								>
									<Input
										type="text"
										value={value.hiit_work_rounds ?? ""}
										onChange={(e) =>
											updateField("hiit_work_rounds", e.target.value)
										}
										className="bg-secondary border-transparent rounded-sm h-9"
										placeholder={t("pbHiitSchemePlaceholder")}
									/>
								</HiitField>
							</>
						)}

						{hiitWorkMode === "amrap" && (
							<HiitField label={t("pbHiitDuration")}>
								<Input
									type="text"
									value={
										value.hiit_work_interval ??
										formatSecondsToClock(value.hiit_work_seconds)
									}
									onChange={(e) =>
										updateHiitClockField(
											"hiit_work_interval",
											"hiit_work_seconds",
											e.target.value,
										)
									}
									className="bg-secondary border-transparent rounded-sm h-9"
									placeholder="8:00"
								/>
							</HiitField>
						)}

						{hiitWorkMode === "custom" && (
							<HiitField
								label={t("pbHiitDescribeBlock")}
								className="w-full md:w-[420px]"
							>
								<Textarea
									value={value.hiit_work_custom_text ?? ""}
									onChange={(e) =>
										updateField("hiit_work_custom_text", e.target.value)
									}
									className="bg-secondary border-transparent rounded-sm text-xs min-h-[64px] resize-y"
									placeholder={t("pbWorkBlockPlaceholder")}
								/>
							</HiitField>
						)}
					</HiitRow>

					{/* Recupero */}
					<HiitRow label={t("hiitRecoveryLabel")}>
						<HiitModeSwitch
							options={hiitRestModeOptions}
							current={hiitRestMode}
							onSelect={(nextMode) => updateField("hiit_rest_mode", nextMode)}
						/>

						{hiitRestMode === "fixed" ? (
							<HiitField label={t("pbHiitDuration")}>
								<Input
									type="text"
									value={
										value.hiit_rest_interval ??
										formatSecondsToClock(value.hiit_rest_seconds)
									}
									onChange={(e) =>
										updateHiitClockField(
											"hiit_rest_interval",
											"hiit_rest_seconds",
											e.target.value,
										)
									}
									className="bg-secondary border-transparent rounded-sm h-9"
									placeholder="0:20"
								/>
							</HiitField>
						) : (
							<>
								<HiitField label={t("pbHiitRatioField")}>
									<Input
										type="text"
										value={value.hiit_rest_ratio ?? ""}
										onChange={(e) =>
											updateField("hiit_rest_ratio", e.target.value)
										}
										onBlur={(e) =>
											setValue((prev) => ({
												...prev,
												hiit_rest_ratio:
													normalizeSmartRatioInput(e.target.value) || "",
											}))
										}
										className="bg-secondary border-transparent rounded-sm h-9"
										placeholder="2:1"
									/>
								</HiitField>
								<HiitField label={t("pbHiitRatioResult")}>
									<div className="h-9 px-2.5 flex items-center rounded-sm border border-dashed border-border text-xs font-mono text-muted-foreground">
										{hiitRestSeconds
											? `= ${formatSecondsToClock(hiitRestSeconds)}`
											: "—"}
									</div>
								</HiitField>
							</>
						)}
					</HiitRow>

					{/* Anteprima — gli stessi dati che vedra' l'atleta */}
					<HiitRow label={t("pbHiitPreview")}>
						<div className="flex flex-wrap items-center gap-2 md:pt-[18px]">
							{!hiitTouched && (
								<span className="text-xs text-muted-foreground">
									{t("pbHiitPreviewEmpty")}
								</span>
							)}

							{hiitHasCycles && (
								<Badge
									variant="outline"
									className="text-xs rounded-sm pointer-events-none select-none"
								>
									{hiitCycles}{" "}
									{hiitCycles === 1
										? t("pbHiitCycleOne")
										: t("pbHiitCycleMany")}
								</Badge>
							)}

							{hiitWorkMode === "custom" ? (
								<Badge
									variant="outline"
									className="text-xs rounded-sm pointer-events-none select-none border-volt/40 text-volt"
								>
									<span className="uppercase tracking-wide text-[10px] opacity-70 mr-1">
										{t("hiitWorkLabel")}
									</span>
									{t("pbCustomLabel")}
								</Badge>
							) : hiitWorkSeconds ? (
								<Badge
									variant="outline"
									className="text-xs rounded-sm pointer-events-none select-none border-volt/40 text-volt"
								>
									<span className="uppercase tracking-wide text-[10px] opacity-70 mr-1">
										{t("hiitWorkLabel")}
									</span>
									{formatSecondsToClock(hiitWorkSeconds)}
									{hiitWorkMode === "amrap"
										? " AMRAP"
										: String(value.hiit_work_rounds || "").trim()
											? ` · ${String(value.hiit_work_rounds).trim()}`
											: ""}
								</Badge>
							) : (
								hiitTouched && (
									<Badge
										variant="outline"
										className="text-xs rounded-sm pointer-events-none select-none border-blaze/50 text-blaze"
									>
										{t("pbHiitMissingWork")}
									</Badge>
								)
							)}

							{hiitRestSeconds ? (
								<Badge
									variant="outline"
									className="text-xs rounded-sm pointer-events-none select-none"
								>
									<span className="uppercase tracking-wide text-[10px] opacity-70 mr-1">
										{hiitRestMode === "ratio"
											? t("hiitRatioLabel")
											: t("hiitRecoveryLabel")}
									</span>
									{hiitRestMode === "ratio"
										? `${normalizeSmartRatioInput(value.hiit_rest_ratio)} → ${formatSecondsToClock(hiitRestSeconds)}`
										: formatSecondsToClock(hiitRestSeconds)}
								</Badge>
							) : (
								hiitTouched && (
									<Badge
										variant="outline"
										className="text-xs rounded-sm pointer-events-none select-none border-blaze/50 text-blaze"
									>
										{hiitRestMode === "ratio" && !hiitRatio
											? t("pbHiitInvalidRatio")
											: t("pbHiitMissingRest")}
									</Badge>
								)
							)}

							{hiitTotalSeconds && (
								<Badge
									variant="outline"
									className="text-xs rounded-sm pointer-events-none select-none border-dashed text-muted-foreground"
								>
									≈ {formatSecondsToClock(hiitTotalSeconds)}{" "}
									{t("pbHiitTotalApprox")}
								</Badge>
							)}
						</div>
					</HiitRow>
				</div>
			)}

			{/* Free WOD */}
			{(value.conditioning_type === "free" ||
				value.conditioning_type === "custom") && (
				<div className="space-y-2">
					<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
						<span className="text-xs text-muted-foreground leading-relaxed">
							{t("pbWodCustomDescription")}
						</span>
						<Button
							type="button"
							size="sm"
							variant="outline"
							className="h-8 px-3 gap-1.5 text-xs border-dashed border-acid/50 text-acid hover:text-acid/80 hover:bg-acid/10 rounded-sm self-start"
							disabled={aiParsing || !value.conditioning_free_text?.trim()}
							onClick={handleParseConditioning}
						>
							{aiParsing ? (
								<>
									<Loader2 className="w-3.5 h-3.5 animate-spin" />
									{t("pbAiParsing")}
								</>
							) : (
								<>
									<Wand2 className="w-3.5 h-3.5" />
									{t("pbAiWand")}
								</>
							)}
						</Button>
						{isCoachAiDisabled && (
							<p className="text-[11px] text-blaze">
								{t("pbAiWandDisabledForCoach")}
							</p>
						)}
					</div>
					<Textarea
						value={value.conditioning_free_text ?? ""}
						onChange={(e) =>
							updateField("conditioning_free_text", e.target.value)
						}
						onKeyDown={handleCustomConditioningTab}
						className="bg-secondary border-transparent rounded-sm text-xs min-h-[120px] font-mono resize-y md:resize overflow-auto"
						placeholder={
							"Es:\n3 Rounds For Time:\n  400m Run\n  21 KB Swing 24kg\n  12 Pull-Up\n\noppure qualsiasi schema..."
						}
					/>
				</div>
			)}

			{/* Conditioning exercises list (AMRAP / For Time — EMOM uses round builder) */}
			{value.conditioning_type &&
				value.conditioning_type !== "free" &&
				value.conditioning_type !== "custom" &&
				value.conditioning_type !== "emom" &&
				!(
					value.conditioning_type === "hiit" &&
					value.hiit_work_mode === "custom"
				) && (
					<div className="space-y-2">
						<Label className="label-tactical">{t("exercisesInWod")}</Label>
						{(value.conditioning_exercises || []).map((ce, ci) => {
							const isActive = activeCondIdx === ci;
							const filteredExercises = isActive
								? rankAndFilterExercises(
										scopeFilteredExercises,
										condSearchQuery,
									)
								: [];
							const condExactMatch =
								isActive &&
								scopeFilteredExercises.some(
									(ex) =>
										ex.name.toLowerCase() ===
										condSearchQuery.trim().toLowerCase(),
								);
							const selectedExercise = ce.exercise_id
								? availableExercises.find(
										(ex) => ex.exercise_id === ce.exercise_id,
									)
								: null;
							const selectedCategory = ce.exercise_id
								? availableExercises.find(
										(ex) => ex.exercise_id === ce.exercise_id,
									)?.category
								: null;
							// Load shown for everything except endurance (ergometers have no weight).
							const showLoad = !isEnduranceCategory(selectedCategory);

							return (
								<div key={ci} className="flex flex-col gap-1">
									{/* Larghezze in em: seguono la dimensione del testo scelta
									    nelle preferenze, cosi' i placeholder "Carico Uomo/Donna"
									    e "Cal/m Uomo/Donna" restano leggibili per intero. */}
									<div className="flex flex-wrap md:flex-nowrap items-center gap-2">
										<span className="text-xs text-muted-foreground w-4">
											{ci + 1}.
										</span>
										<Input
											value={ce.reps ?? ""}
											onChange={(e) =>
												updateExerciseRow(ci, "reps", e.target.value)
											}
											className={`bg-secondary border-transparent rounded-sm h-8 text-xs ${
												showLoad ? "w-[5.5em]" : "w-[11em]"
											}`}
											placeholder={getConditioningQuantityPlaceholder(
												selectedCategory,
											)}
											title={
												isEnduranceCategory(selectedCategory)
													? t("pbCaloriesMaleFemale")
													: t("reps")
											}
											aria-label={
												isEnduranceCategory(selectedCategory)
													? t("pbCaloriesMaleFemale")
													: t("reps")
											}
										/>
										<div
											className="relative flex-1 min-w-[55%] md:min-w-[200px]"
											ref={isActive ? condSearchRef : undefined}
										>
											{ce.exercise_id && !isActive ? (
												<button
													type="button"
													onClick={() => {
														setActiveCondIdx(ci);
														setCondSearchQuery(ce.exercise_name ?? "");
													}}
													className="w-full flex items-center gap-2 bg-secondary rounded-sm h-8 px-2 text-left hover:bg-secondary/80"
												>
													<Dumbbell className="w-3 h-3 text-volt flex-shrink-0" />
													<span className="text-xs truncate flex-1">
														{ce.exercise_name}
													</span>
													{selectedExercise && (
														<>
															<span
																title={getExerciseScopeLabel(selectedExercise)}
																className={`md:hidden w-1.5 h-1.5 rounded-full flex-shrink-0 ${
																	getExerciseLibraryScope(selectedExercise) ===
																	"global"
																		? "bg-amber-400"
																		: "bg-emerald-400"
																}`}
															/>
															<Badge
																className={`hidden md:inline-flex flex-shrink-0 border-0 text-[11px] px-1.5 py-0 ${
																	getExerciseLibraryScope(selectedExercise) ===
																	"global"
																		? "bg-amber-500/20 text-amber-300"
																		: "bg-emerald-500/20 text-emerald-300"
																}`}
															>
																{getExerciseScopeLabel(selectedExercise)}
															</Badge>
														</>
													)}
													<Search className="w-3 h-3 text-muted-foreground flex-shrink-0" />
												</button>
											) : (
												<>
													<Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
													<Input
														value={
															isActive
																? condSearchQuery
																: (ce.exercise_name ?? "")
														}
														onChange={(e) => {
															setCondSearchQuery(e.target.value);
															setActiveCondIdx(ci);
															if (ce.exercise_id) {
																updateExerciseRow(ci, "exercise_id", "");
																updateExerciseRow(ci, "exercise_name", "");
																updateExerciseRow(ci, "load", "");
															}
														}}
														onFocus={() => {
															setActiveCondIdx(ci);
															setCondSearchQuery(ce.exercise_name ?? "");
														}}
														className="bg-secondary border-transparent rounded-sm h-8 text-xs pl-7"
														placeholder={t("searchExercisePlaceholder")}
														autoFocus={isActive}
													/>
												</>
											)}
											{isActive && (
												<div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-sm shadow-lg">
													<ScrollArea className="h-48">
														{filteredExercises.length > 0 ? (
															<div className="p-1">
																{filteredExercises.slice(0, 50).map((ex) => (
																	<div
																		key={ex.exercise_id}
																		className="flex items-center gap-1 px-1 rounded-sm"
																	>
																		<button
																			type="button"
																			onClick={() => {
																				updateExerciseRow(
																					ci,
																					"exercise_id",
																					ex.exercise_id,
																				);
																				updateExerciseRow(
																					ci,
																					"exercise_name",
																					ex.name,
																				);
																				setActiveCondIdx(-1);
																				setCondSearchQuery("");
																				setVideoPreviewState(null);
																			}}
																			className="flex-1 text-left px-2 py-1.5 rounded-sm hover:bg-secondary flex items-center gap-2"
																		>
																			<Dumbbell className="w-3 h-3 text-volt flex-shrink-0" />
																			<div className="flex-1 min-w-0">
																				<p className="text-xs font-medium truncate">
																					{ex.name}
																				</p>
																				<div className="flex items-center gap-1.5">
																					<p className="text-[10px] text-muted-foreground">
																						{ex.category}
																					</p>
																					<Badge
																						className={`border-0 text-[11px] px-1.5 py-0 ${
																							getExerciseLibraryScope(ex) ===
																							"global"
																								? "bg-amber-500/20 text-amber-300"
																								: "bg-emerald-500/20 text-emerald-300"
																						}`}
																					>
																						{getExerciseScopeLabel(ex)}
																					</Badge>
																				</div>
																			</div>
																		</button>
																		<Button
																			type="button"
																			variant="ghost"
																			size="icon"
																			className="h-6 w-6 text-muted-foreground hover:text-volt"
																			disabled={
																				!getYouTubeEmbedUrl(ex.video_url)
																			}
																			onClick={(event) => {
																				event.stopPropagation();
																				openExerciseVideoPreview(
																					`conditioning-row-${ci}`,
																					ex,
																				);
																			}}
																		>
																			<Play className="w-3 h-3" />
																		</Button>
																	</div>
																))}
															</div>
														) : null}

														{!condExactMatch && condSearchQuery.trim() && (
															<div className="border-t border-border p-1">
																<button
																	type="button"
																	onClick={() => handleOpenCreateExercise(ci)}
																	className="w-full text-left px-3 py-1.5 rounded-sm hover:bg-volt/10 flex items-center gap-2 text-volt"
																>
																	<PlusCircle className="w-3 h-3 flex-shrink-0" />
																	<span className="text-xs font-medium">
																		Aggiungi &quot;{condSearchQuery}&quot; alla
																		libreria personale
																	</span>
																</button>
															</div>
														)}

														{filteredExercises.length === 0 &&
															!condSearchQuery.trim() && (
																<div className="p-3 text-center text-xs text-muted-foreground">
																	{t("pbStartTypingToSearch")}
																</div>
															)}
													</ScrollArea>
												</div>
											)}
											{isActive &&
												renderInlineVideoPreview(`conditioning-row-${ci}`)}
										</div>
										{showLoad && (
											<Input
												value={ce.load ?? ""}
												onChange={(e) =>
													updateExerciseRow(ci, "load", e.target.value)
												}
												className="bg-secondary border-transparent rounded-sm h-8 text-xs w-[11.5em]"
												placeholder={t("pbLoadMaleFemale")}
												aria-label={t("pbLoadMaleFemale")}
												title={t("pbLoadMaleFemale")}
											/>
										)}
										<Button
											variant="ghost"
											size="icon"
											className="h-7 w-7 text-muted-foreground hover:text-foreground flex-shrink-0"
											onClick={() => moveExerciseRow(ci, -1)}
											disabled={ci === 0}
											title={t("pbActionMoveUp")}
										>
											<ArrowUp className="w-3 h-3" />
										</Button>
										<Button
											variant="ghost"
											size="icon"
											className="h-7 w-7 text-muted-foreground hover:text-foreground flex-shrink-0"
											onClick={() => moveExerciseRow(ci, 1)}
											disabled={
												ci === (value.conditioning_exercises || []).length - 1
											}
											title={t("moveDown")}
										>
											<ArrowDown className="w-3 h-3" />
										</Button>
										<Button
											variant="ghost"
											size="icon"
											className="h-7 w-7 text-muted-foreground hover:text-blaze flex-shrink-0"
											onClick={() => removeExerciseRow(ci)}
										>
											<X className="w-3 h-3" />
										</Button>
									</div>
									{/* La nota e' una sola, su tutto l'esercizio: qui non se ne
									    scrivono di nuove. Quelle gia' nei programmi l'atleta le
									    legge sotto la riga del WOD, quindi restano visibili con
									    la X per toglierle. */}
									{String(ce.notes || "").trim() && (
										<div className="flex items-start gap-1 ml-6">
											<span className="text-[11px] text-muted-foreground italic">
												{t("pbMovementNote")}: {String(ce.notes).trim()}
											</span>
											<Button
												variant="ghost"
												size="icon"
												className="h-6 w-6 flex-shrink-0 text-muted-foreground hover:text-blaze"
												title={t("pbRemoveMovementNote")}
												onClick={() => updateExerciseRow(ci, "notes", "")}
											>
												<X className="w-3 h-3" />
												<span className="sr-only">
													{t("pbRemoveMovementNote")}
												</span>
											</Button>
										</div>
									)}
								</div>
							);
						})}
						<Button
							variant="ghost"
							size="sm"
							className="w-full border border-dashed border-border rounded-sm h-7 text-xs text-muted-foreground"
							onClick={addExerciseRow}
						>
							<Plus className="w-3 h-3 mr-1" />
							{t("addExerciseButton")}
						</Button>
					</div>
				)}

			{/* Create new exercise dialog */}
			<Dialog
				open={isCreatingExercise}
				onOpenChange={(open) => {
					if (!open) {
						setIsCreatingExercise(false);
						setCreateExerciseIdx(-1);
					}
				}}
			>
				<DialogContent className="bg-card border-border w-[calc(100vw-2rem)] max-w-md">
					<DialogDescription className="hidden" />
					<DialogHeader>
						<DialogTitle className="font-heading">{t("newExercise")}</DialogTitle>
					</DialogHeader>
					<div className="space-y-4">
						<div className="space-y-2">
							<Label className="label-tactical">{t("exerciseName")}</Label>
							<Input
								value={newExerciseDetails.name}
								onChange={(e) =>
									setNewExerciseDetails({
										...newExerciseDetails,
										name: e.target.value,
									})
								}
								className="bg-secondary border-transparent rounded-sm"
								placeholder={t("exerciseNamePlaceholder")}
								autoFocus
							/>
						</div>
						<div className="space-y-2">
							<Label className="label-tactical">{t("category")}</Label>
							<Select
								value={newExerciseDetails.category}
								onValueChange={(v) =>
									setNewExerciseDetails({
										...newExerciseDetails,
										category: v,
									})
								}
							>
								<SelectTrigger className="bg-secondary border-transparent rounded-sm">
									<SelectValue />
								</SelectTrigger>
								<SelectContent className="bg-card border-border">
									<SelectItem value="strength">{t("strength")}</SelectItem>
									<SelectItem value="cardio">{t("cardio")}</SelectItem>
									<SelectItem value="gymnastics">{t("gymnastics")}</SelectItem>
									<SelectItem value="olympic">{t("olympic")}</SelectItem>
									<SelectItem value="accessory">{t("accessory")}</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-2">
							<Label className="label-tactical">{t("pbDescriptionOptional")}</Label>
							<Textarea
								value={newExerciseDetails.description}
								onChange={(e) =>
									setNewExerciseDetails({
										...newExerciseDetails,
										description: e.target.value,
									})
								}
								className="bg-secondary border-transparent rounded-sm min-h-[60px]"
								placeholder={t("exerciseDescriptionPlaceholder")}
							/>
						</div>
						<div className="space-y-2">
							<Label className="label-tactical">{t("pbVideoUrlOptional")}</Label>
							<Input
								value={newExerciseDetails.video_url}
								onChange={(e) =>
									setNewExerciseDetails({
										...newExerciseDetails,
										video_url: e.target.value,
									})
								}
								className="bg-secondary border-transparent rounded-sm"
								placeholder="https://youtube.com/..."
							/>
						</div>
						<div className="flex gap-2 pt-2">
							<Button
								variant="outline"
								onClick={() => {
									setIsCreatingExercise(false);
									setCreateExerciseIdx(-1);
								}}
								className="flex-1 rounded-sm"
							>
								{t("cancel")}
							</Button>
							<Button
								onClick={handleCreateExercise}
								disabled={savingExercise || !newExerciseDetails.name.trim()}
								className="flex-1 bg-volt hover:bg-volt/90 rounded-sm"
							>
								{savingExercise ? t("pbSaving") : t("pbCreateAndSelect")}
							</Button>
						</div>
					</div>
				</DialogContent>
			</Dialog>

			{/* Notes for non-EMOM types */}
			{value.conditioning_type && value.conditioning_type !== "emom" && (
				<div className="space-y-1">
					<span className="text-[10px] text-muted-foreground uppercase tracking-wider">
						{t("pbWodNotes")}
					</span>
					<Textarea
						value={value.notes ?? ""}
						onChange={(e) => updateField("notes", e.target.value)}
						className="bg-secondary border-transparent rounded-sm text-xs min-h-[48px] resize-none"
						placeholder={t("pbWodNotesPlaceholder")}
					/>
				</div>
			)}

			{/* Optional result schema — available for all conditioning types */}
			{value.conditioning_type && (
				<div
					ref={resultSchemaRef}
					className={`rounded-sm transition-colors ${
						highlightResultSchema ? "ring-1 ring-volt/70 bg-volt/5 p-1" : ""
					}`}
				>
					<ConditioningResultSchemaEditor
						value={value.result_schema || []}
						onChange={(next) => updateField("result_schema", next)}
						defaultUnit={
							value.conditioning_type === "for_time" ? "time_or_reps" : "reps"
						}
						disabled={schemaLocked}
						disabledHint={schemaLockedHint}
					/>
				</div>
			)}
		</div>
	);
}
